/*
  Emm's Plant House — Plant Tools Shared State Foundation
  File: assets/emms-tool-state.js
  Version: 1.2.0

  Purpose:
  - One shared URL/local state object for all plant tools.
  - Keeps plant, problem, treatment, bio_control/control and resolver_state aligned.
  - Builds safe cross-tool URLs.
  - Prevents links carrying one problem while resolver_state carries another.
*/
(function () {
  'use strict';

  if (window.EmmsToolState && window.EmmsToolState.version) return;

  var VERSION = '1.2.0';
  var STORAGE_KEY = 'emms:plant-tools:state:v1';

  var EMPTY_RESOLVER_STATE = {
    plant: '',
    problem: '',
    issue: '',
    treatment: '',
    bio_control: '',
    control: '',
    selectedObservationIds: [],
    rejectedObservationIds: [],
    confirmationAnswers: {},
    contextAnswers: {},
    activeObservationGroup: 'all',
    pinnedObservationGroup: 'all',
    groupPinned: false,
    forceShowMore: false,
    notConvincedMode: false
  };

  function clone(value) {
    try {
      return JSON.parse(JSON.stringify(value || {}));
    } catch (err) {
      return {};
    }
  }

  function safeDecode(value) {
    if (value == null) return '';
    try {
      return decodeURIComponent(String(value).replace(/\+/g, ' '));
    } catch (err) {
      return String(value);
    }
  }

  function normaliseHandle(value) {
    var raw = safeDecode(value).trim();

    if (!raw) return '';

    return raw
      .toLowerCase()
      .replace(/&amp;/g, 'and')
      .replace(/&/g, 'and')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function normaliseText(value) {
    return safeDecode(value).trim();
  }

  function parseResolverState(value) {
    if (!value) return clone(EMPTY_RESOLVER_STATE);

    try {
      var parsed = JSON.parse(safeDecode(value));
      if (!parsed || typeof parsed !== 'object') return clone(EMPTY_RESOLVER_STATE);
      return Object.assign(clone(EMPTY_RESOLVER_STATE), parsed);
    } catch (err) {
      return clone(EMPTY_RESOLVER_STATE);
    }
  }

  function readLocalState() {
    try {
      if (!window.localStorage) return {};
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  function readInitialState() {
    var params = new URLSearchParams(window.location.search || '');
    var stateKeys = ['plant', 'problem', 'issue', 'treatment', 'bio_control', 'control', 'biocontrol', 'resolver_state'];
    var hasUrlState = stateKeys.some(function (key) { return params.has(key); });
    var local = hasUrlState ? {} : readLocalState();

    var resolverSource = params.has('resolver_state') ? params.get('resolver_state') : local.resolver_state;
    var resolver = parseResolverState(resolverSource);

    var directPlant = params.has('plant');
    var directProblem = params.has('problem') || params.has('issue');
    var directTreatment = params.has('treatment');
    var directBio = params.has('bio_control') || params.has('control') || params.has('biocontrol');

    var plant = normaliseHandle(directPlant ? params.get('plant') : (resolver.plant || local.plant || ''));
    var problem = normaliseHandle(directProblem ? (params.get('problem') || params.get('issue') || '') : (resolver.problem || resolver.issue || local.problem || local.issue || ''));
    var treatment = normaliseHandle(directTreatment ? params.get('treatment') : ((directPlant || directProblem) ? '' : (resolver.treatment || local.treatment || '')));
    var bioControl = normaliseHandle(
      directBio
        ? (params.get('bio_control') || params.get('control') || params.get('biocontrol') || '')
        : ((directPlant || directProblem) ? '' : (resolver.bio_control || resolver.control || local.bio_control || local.control || ''))
    );

    resolver.plant = plant;
    resolver.problem = problem;
    resolver.issue = problem;
    resolver.treatment = treatment;
    resolver.bio_control = bioControl;
    resolver.control = bioControl;

    return normaliseState({
      plant: plant,
      problem: problem,
      issue: problem,
      treatment: treatment,
      bio_control: bioControl,
      control: bioControl,
      resolver_state: resolver
    });
  }

  function persist(nextState) {
    try {
      if (!window.localStorage) return;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
    } catch (err) {
      // Non-blocking. Private browsing or consent tools may block storage.
    }
  }

  function broadcast(nextState, previousState) {
    window.dispatchEvent(new CustomEvent('emms:tool-state:change', {
      detail: {
        state: clone(nextState),
        previousState: clone(previousState)
      }
    }));
  }

  function normaliseState(input) {
    var next = Object.assign({}, input || {});

    next.plant = normaliseHandle(next.plant || '');
    next.problem = normaliseHandle(next.problem || next.issue || '');
    next.issue = next.problem;
    next.treatment = normaliseHandle(next.treatment || '');
    next.bio_control = normaliseHandle(next.bio_control || next.control || '');
    next.control = next.bio_control;

    var resolver = Object.assign(clone(EMPTY_RESOLVER_STATE), clone(next.resolver_state || {}));
    resolver.plant = next.plant;
    resolver.problem = next.problem;
    resolver.issue = next.problem;
    resolver.treatment = next.treatment;
    resolver.bio_control = next.bio_control;
    resolver.control = next.bio_control;

    next.resolver_state = resolver;

    return next;
  }

  var state = readInitialState();

  function get() {
    return clone(state);
  }

  function getResolverState() {
    var resolver = Object.assign(clone(EMPTY_RESOLVER_STATE), clone(state.resolver_state || {}));
    resolver.plant = normaliseHandle(state.plant || resolver.plant || '');
    resolver.problem = normaliseHandle(state.problem || resolver.problem || '');
    return resolver;
  }

  function set(patch, options) {
    options = options || {};
    patch = patch || {};

    var previous = clone(state);
    var prepared = Object.assign({}, patch);

    var hasPlantPatch = Object.prototype.hasOwnProperty.call(prepared, 'plant');
    var hasProblemPatch = Object.prototype.hasOwnProperty.call(prepared, 'problem') || Object.prototype.hasOwnProperty.call(prepared, 'issue');
    var hasTreatmentPatch = Object.prototype.hasOwnProperty.call(prepared, 'treatment');
    var hasBioPatch = Object.prototype.hasOwnProperty.call(prepared, 'bio_control') || Object.prototype.hasOwnProperty.call(prepared, 'control');

    var nextPlant = hasPlantPatch ? normaliseHandle(prepared.plant || '') : state.plant;
    var nextProblem = hasProblemPatch ? normaliseHandle(prepared.problem || prepared.issue || '') : state.problem;

    if (hasPlantPatch && nextPlant !== state.plant) {
      if (!hasProblemPatch) {
        prepared.problem = '';
        prepared.issue = '';
      }
      if (!hasTreatmentPatch) prepared.treatment = '';
      if (!hasBioPatch) {
        prepared.bio_control = '';
        prepared.control = '';
      }
    }

    if (hasProblemPatch && nextProblem !== state.problem) {
      prepared.problem = nextProblem;
      prepared.issue = nextProblem;
      if (!hasTreatmentPatch) prepared.treatment = '';
      if (!hasBioPatch) {
        prepared.bio_control = '';
        prepared.control = '';
      }
    }

    var next = normaliseState(Object.assign({}, state, prepared));
    state = next;

    if (!options.skipPersist) persist(state);
    if (!options.silent) broadcast(state, previous);

    return get();
  }

  function mergeResolverState(patch, options) {
    var resolver = Object.assign(getResolverState(), patch || {});
    var problem = normaliseHandle(resolver.problem || resolver.issue || state.problem || '');
    var bioControl = normaliseHandle(resolver.bio_control || resolver.control || state.bio_control || '');
    return set({
      resolver_state: resolver,
      plant: normaliseHandle(resolver.plant || state.plant || ''),
      problem: problem,
      issue: problem,
      treatment: normaliseHandle(resolver.treatment || state.treatment || ''),
      bio_control: bioControl,
      control: bioControl
    }, options);
  }

  function toParams(patch) {
    patch = patch || {};
    var prepared = Object.assign({}, patch);
    var hasPlantPatch = Object.prototype.hasOwnProperty.call(prepared, 'plant');
    var hasProblemPatch = Object.prototype.hasOwnProperty.call(prepared, 'problem') || Object.prototype.hasOwnProperty.call(prepared, 'issue');
    var hasTreatmentPatch = Object.prototype.hasOwnProperty.call(prepared, 'treatment');
    var hasBioPatch = Object.prototype.hasOwnProperty.call(prepared, 'bio_control') || Object.prototype.hasOwnProperty.call(prepared, 'control');
    var nextPlant = hasPlantPatch ? normaliseHandle(prepared.plant || '') : state.plant;
    var nextProblem = hasProblemPatch ? normaliseHandle(prepared.problem || prepared.issue || '') : state.problem;

    if (hasPlantPatch && nextPlant !== state.plant) {
      if (!hasProblemPatch) {
        prepared.problem = '';
        prepared.issue = '';
      }
      if (!hasTreatmentPatch) prepared.treatment = '';
      if (!hasBioPatch) {
        prepared.bio_control = '';
        prepared.control = '';
      }
    }
    if (hasProblemPatch && nextProblem !== state.problem) {
      prepared.problem = nextProblem;
      prepared.issue = nextProblem;
      if (!hasTreatmentPatch) prepared.treatment = '';
      if (!hasBioPatch) {
        prepared.bio_control = '';
        prepared.control = '';
      }
    }

    var next = normaliseState(Object.assign({}, state, prepared));
    var params = new URLSearchParams();

    if (next.plant) params.set('plant', next.plant);
    if (next.problem) {
      params.set('problem', next.problem);
      params.set('issue', next.problem);
    }
    if (next.treatment) params.set('treatment', next.treatment);

    if (next.bio_control) {
      params.set('bio_control', next.bio_control);
      params.set('control', next.bio_control);
    }

    var resolver = Object.assign(clone(EMPTY_RESOLVER_STATE), clone(next.resolver_state || {}));
    resolver.plant = next.plant || '';
    resolver.problem = next.problem || '';
    resolver.issue = resolver.problem;
    resolver.treatment = next.treatment || '';
    resolver.bio_control = next.bio_control || '';
    resolver.control = resolver.bio_control;

    params.set('resolver_state', JSON.stringify(resolver));
    return params;
  }

  function makeUrl(path, patch) {
    var safePath = String(path || window.location.pathname || '/');
    var url = new URL(safePath, window.location.origin);
    var params = toParams(patch || {});

    params.forEach(function (value, key) {
      url.searchParams.set(key, value);
    });

    return url.pathname + url.search + url.hash;
  }

  function replaceUrl(patch) {
    var params = toParams(patch || {});
    var nextUrl = window.location.pathname + '?' + params.toString() + window.location.hash;

    window.history.replaceState({}, '', nextUrl);
    set(patch || {}, { silent: false });

    return nextUrl;
  }

  function pushUrl(path, patch) {
    var nextUrl = makeUrl(path || window.location.pathname, patch || {});

    window.history.pushState({}, '', nextUrl);
    set(patch || {}, { silent: false });

    return nextUrl;
  }

  function statePatchFromElement(element) {
    var patch = {};

    if (!element || !element.getAttribute) return patch;

    if (element.hasAttribute('data-emms-plant')) {
      patch.plant = element.getAttribute('data-emms-plant');
    }

    if (element.hasAttribute('data-emms-problem')) {
      patch.problem = element.getAttribute('data-emms-problem');
    }

    if (element.hasAttribute('data-emms-treatment')) {
      patch.treatment = element.getAttribute('data-emms-treatment');
    }

    if (element.hasAttribute('data-emms-bio-control')) {
      patch.bio_control = element.getAttribute('data-emms-bio-control');
    }

    if (element.hasAttribute('data-emms-control')) {
      patch.bio_control = element.getAttribute('data-emms-control');
    }

    return patch;
  }

  function wireLinks(root) {
    root = root || document;

    var links = root.querySelectorAll('[data-emms-state-link]');

    links.forEach(function (link) {
      var path = link.getAttribute('data-emms-state-link') || link.getAttribute('href') || window.location.pathname;
      var patch = statePatchFromElement(link);

      link.setAttribute('href', makeUrl(path, patch));
    });
  }

  function reset(options) {
    options = options || {};
    var previous = clone(state);

    state = normaliseState({
      plant: '',
      problem: '',
      treatment: '',
      bio_control: '',
      control: '',
      resolver_state: clone(EMPTY_RESOLVER_STATE)
    });

    try {
      if (window.localStorage) window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // noop
    }

    if (!options.silent) broadcast(state, previous);

    return get();
  }

  window.addEventListener('popstate', function () {
    var previous = clone(state);
    state = readInitialState();
    broadcast(state, previous);
  });

  document.addEventListener('DOMContentLoaded', function () {
    wireLinks(document);
  });

  window.EmmsToolState = {
    version: VERSION,
    get: get,
    set: set,
    reset: reset,
    normaliseHandle: normaliseHandle,
    normaliseText: normaliseText,
    getResolverState: getResolverState,
    mergeResolverState: mergeResolverState,
    toParams: toParams,
    makeUrl: makeUrl,
    replaceUrl: replaceUrl,
    pushUrl: pushUrl,
    wireLinks: wireLinks
  };
}());